import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ASIGNACIONES_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Genera la lista de posiciones (Integrantes y Suplentes) con base en
 * maxMiembros y numeroSuplentes.
 */
function getPosicionesOptions(maxMiembros, numeroSuplentes, permitirSuplentes) {
  const result = [];
  // Integrantes
  for (let i = 1; i <= maxMiembros; i++) {
    result.push(`Integrante ${i}`);
  }
  // Suplentes
  if (permitirSuplentes) {
    for (let s = 1; s <= numeroSuplentes; s++) {
      result.push(`Suplente ${s}`);
    }
  }
  // Opcional: se podría agregar un valor vacío al inicio, si se desea
  return result;
}

const StepRelevos = ({
  formik,
  handleBack,
  handleNext,
  opcionesRelevos = {},
  equiposDisponibles = []
}) => {
  const {
    maxMiembros = 0,
    numeroSuplentes = 0,
    permitirSuplentes = false,
    permitirEquiposMixtos = false,
  } = opcionesRelevos;

  const relevos = formik.values.relevosData || [];
  const rama = (formik.values.rama || '').toLowerCase();

  // 1) Efecto para inicializar un relevo vacío si no existe
  useEffect(() => {
    if (relevos.length === 0) {
      const newRelevo = {
        prueba: '',
        tipoMarca: '',
        asignacion: '',
        posicion: '',
        equipoTipo: ''  // se autocompletará
      };
      formik.setFieldValue('relevosData', [newRelevo], false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Efecto para preseleccionar el equipoTipo con base en la rama,
  //    solo si equipoTipo está vacío (para evitar loops)
  useEffect(() => {
    if (relevos.length === 0) return;

    let defaultTipo = '';
    if (rama.includes('var')) {
      defaultTipo = 'Varonil';
    } else if (rama.includes('fem')) {
      defaultTipo = 'Femenil';
    } else if (permitirEquiposMixtos) {
      defaultTipo = 'Mixto';
    } else {
      // fallback
      defaultTipo = 'Varonil';
    }

    const updated = relevos.map((rel) => {
      if (!rel.equipoTipo) {
        return { ...rel, equipoTipo: defaultTipo };
      }
      return rel;
    });

    // Verificamos si en verdad hubo un cambio
    const hasChanged = updated.some((rel, i) => rel.equipoTipo !== relevos[i].equipoTipo);
    if (hasChanged) {
      formik.setFieldValue('relevosData', updated, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rama, permitirEquiposMixtos]);

  // Lógica de cambio del equipoTipo manual
  const handleChangeEquipoTipo = (index, newValue) => {
    if (relevos[index].equipoTipo !== newValue) {
      const updated = [...relevos];
      updated[index] = { ...updated[index], equipoTipo: newValue };
      formik.setFieldValue('relevosData', updated);
    }
  };

  // Generamos las opciones de "posiciones" dinámicamente
  const posiciones = getPosicionesOptions(maxMiembros, numeroSuplentes, permitirSuplentes);
  // Ensure integrantes and suplentes are not initialized unless needed
useEffect(() => {
  const updated = (formik.values.relevosData || []).map((relevo) => {
    if (!relevo.posicion && (relevo.integrantes || relevo.suplentes)) {
      const { integrantes, suplentes, ...rest } = relevo;
      return rest;
    }
    return relevo;
  });

  const prev = formik.values.relevosData || [];
  const changed = JSON.stringify(updated) !== JSON.stringify(prev);
  if (changed) {
    formik.setFieldValue('relevosData', updated, false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  return (
    <>
      <Box>
        {relevos.map((relevo, index) => (
          <Accordion key={index} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                Relevo #{index + 1}
                {relevo.prueba ? `: ${relevo.prueba}` : ''}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* 2.1) Tipo de Equipo */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Tipo de Equipo</Typography>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <RadioGroup
                      row
                      value={relevo.equipoTipo || ''}
                      onChange={(e) => handleChangeEquipoTipo(index, e.target.value)}
                    >
                      {(() => {
                        // Calculamos qué opciones mostrar
                        if (rama.includes('var')) {
                          // Solo Varonil y, si se permite, Mixto
                          return permitirEquiposMixtos
                            ? ['Varonil', 'Mixto']
                            : ['Varonil'];
                        } else if (rama.includes('fem')) {
                          // Solo Femenil y, si se permite, Mixto
                          return permitirEquiposMixtos
                            ? ['Femenil', 'Mixto']
                            : ['Femenil'];
                        } else {
                          // Fallback
                          return permitirEquiposMixtos
                            ? ['Varonil', 'Femenil', 'Mixto']
                            : ['Varonil', 'Femenil'];
                        }
                      })().map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt}
                          control={<Radio size="small" />}
                          label={opt}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* 2.2) Asignación */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Asignación</Typography>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Letra</InputLabel>
                    <Select
                      label="Letra"
                      value={relevo.asignacion || ''}
                      onChange={(e) => {
                        const updated = [...relevos];
                        updated[index] = {
                          ...updated[index],
                          asignacion: e.target.value
                        };
                        formik.setFieldValue('relevosData', updated);
                      }}
                      size="small"
                    >
                      {ASIGNACIONES_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 2.3) Posición en el Relevo */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Posición en el Relevo</Typography>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Posición</InputLabel>
                    <Select
                      label="Posición"
                      value={relevo.posicion || ''}
                      onChange={(e) => {
                        const updated = [...relevos];
                        updated[index] = {
                          ...updated[index],
                          posicion: e.target.value
                        };
                        formik.setFieldValue('relevosData', updated);
                      }}
                      size="small"
                    >
                      <MenuItem value="">(Ninguna)</MenuItem>
                      {posiciones.map((pos) => (
                        <MenuItem key={pos} value={pos}>
                          {pos}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button onClick={handleBack} sx={{ mr: 1 }}>
            Anterior
          </Button>
          <Button onClick={handleNext} variant="contained" color="primary">
            Siguiente
          </Button>
        </Box>
      </Grid>
    </>
  );
};

StepRelevos.propTypes = {
  formik: PropTypes.object.isRequired,
  opcionesRelevos: PropTypes.shape({
    maxMiembros: PropTypes.number,
    numeroSuplentes: PropTypes.number,
    permitirSuplentes: PropTypes.bool,
    permitirEquiposMixtos: PropTypes.bool
  }),
  equiposDisponibles: PropTypes.array,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default StepRelevos;
